import { Routes, Route, useLocation } from 'react-router-dom';
import PrivacyPolicy from './components/PrivacyPolicy';

import React, { useState, useEffect } from 'react';
import { auth, db, googleProvider } from './firebase';
import { signInWithPopup, onAuthStateChanged, signOut, User } from 'firebase/auth';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc, deleteField, getDoc, setDoc } from 'firebase/firestore';
import './App.css';
import { Item } from './interfaces';
import { websites } from './websites';
import CartTable from './components/CartTable';
import Header from './components/Header';
import Footer from './components/Footer';
import AboutPopup from './components/AboutPopup';
import QuotaReachedPopup from './components/QuotaReachedPopup';
import GlobalSettingsPopup from './components/GlobalSettingsPopup';
import ValidationPopup from './components/ValidationPopup';
import ExtensionRequiredPopup from './components/ExtensionRequiredPopup';

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);
};

function App() {
  usePageTracking();
  const [user, setUser] = useState<User | null>(null);
  const [showGlobalSettingsPopup, setShowGlobalSettingsPopup] = useState(false);
  const [shoppingItems, setShoppingItems] = useState<Item[]>([]);
  const [newItem, setNewItem] = useState('');
  const [showAboutPopup, setShowAboutPopup] = useState(false);
  const [showExtensionRequiredPopup, setShowExtensionRequiredPopup] = useState(false);
  const [showSmartCartInfo, setShowSmartCartInfo] = useState(false);
  const [maxItemsInSmartCart, setMaxItemsInSmartCart] = useState(5); // Default to 5
  const [geminiApiKey, setGeminiApiKey] = useState<string | null>(null);
  const [showQuotaReachedPopup, setShowQuotaReachedPopup] = useState(false);
  const [itemToDisableAI, setItemToDisableAI] = useState<Item | null>(null); // To store the item for which AI needs to be disabled
  const [showValidationPopup, setShowValidationPopup] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [itemToAddAfterValidation, setItemToAddAfterValidation] = useState<Item | null>(null);
  const [itemToUpdateAfterValidation, setItemToUpdateAfterValidation] = useState<{ item: Item; newName: string } | null>(null);
  const [validationPromiseCallbacks, setValidationPromiseCallbacks] = useState<{ resolve: (value: boolean) => void; reject: () => void } | null>(null);
  const [isGeminiProcessing, setIsGeminiProcessing] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const popupShown = localStorage.getItem('extensionPopupShown');
        if (!popupShown) {
          setShowExtensionRequiredPopup(true);
          localStorage.setItem('extensionPopupShown', 'true');
        }
        // Send a message to the Chrome extension to notify it of the login
        const extensionIds = [
          'omdgkcjegnmmfcfjdgllilkphjgbmooa',
          'bhpgolaacmlhpoflfomangjboeombkkg'
        ];

        if (window.chrome && chrome.runtime && chrome.runtime.sendMessage) {
          extensionIds.forEach(extensionId => {
            chrome.runtime.sendMessage(extensionId, { type: 'authSuccess' }, (response) => {
              if (chrome.runtime.lastError) {
                console.warn('Could not send message to extension. This is expected if the extension is not installed or disabled.', chrome.runtime.lastError);
              } else {
                console.log(`Message sent to extension ${extensionId}:`, response);
              }
            });
          });
        } else {
          console.warn('Chrome runtime is not available. Cannot send message to extension.');
        }
      }
      if (!currentUser) {
        setShoppingItems([]);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchLimitsAndSettings = async () => {
      if (user) {
        if (!db) return;

        // Fetch default settings
        const defaultDocRef = doc(db, 'defaultUserSettings', 'default');
        const defaultDocSnap = await getDoc(defaultDocRef);
        let defaultLimit = 5;
        if (defaultDocSnap.exists()) {
          defaultLimit = defaultDocSnap.data()?.max_items_in_smart_cart || 5;
        }

        let userLimit = defaultLimit;
        const userDocRef = doc(db, 'userSettings', user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          userLimit = userDocSnap.data()?.max_items_in_smart_cart || defaultLimit;
          setGeminiApiKey(userDocSnap.data()?.geminiApiKey || null);
        }
        setMaxItemsInSmartCart(userLimit);
      }
    };

    fetchLimitsAndSettings();
  }, [user]);



  useEffect(() => {
    if (user) {
      const q = query(collection(db, 'cartItems'), where('userId', '==', user.uid));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const allItems: Item[] = [];
        querySnapshot.forEach((doc) => {
          allItems.push({ ...doc.data(), id: doc.id } as Item);
        });
        setShoppingItems(allItems);
      });
      return () => unsubscribe();
    }
  }, [user]);

  const handleGoogleLogin = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Error signing in with Google: ", error);
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem('extensionPopupShown');
      await signOut(auth);
      // Send a message to the Chrome extension to notify it of the logout
      const extensionIds = [
        'omdgkcjegnmmfcfjdgllilkphjgbmooa',
        'bhpgolaacmlhpoflfomangjboeombkkg'
      ];

      if (window.chrome && chrome.runtime && chrome.runtime.sendMessage) {
        extensionIds.forEach(extensionId => {
          chrome.runtime.sendMessage(extensionId, { type: 'authLogout' }, (response) => {
            if (chrome.runtime.lastError) {
              console.warn('Could not send message to extension. This is expected if the extension is not installed or disabled.', chrome.runtime.lastError);
            } else {
              console.log(`Message sent to extension ${extensionId}:`, response);
            }
          });
        });
      } else {
        console.warn('Chrome runtime is not available. Cannot send message to extension.');
      }
    } catch (error) {
      console.error("Error signing out: ", error);
    }
  };



  const handleAddItemConfirmed = async () => {
    if (!user || !itemToAddAfterValidation) return;

    const newItemData = {
      ...itemToAddAfterValidation,
      last_updated: new Date(0).toISOString(),
      is_tracked_periodically: false,
    };

    const docRef = await addDoc(collection(db, 'cartItems'), newItemData);
    window.gtag('event', 'item_added', { item_id: docRef.id, item_name: newItemData.item_name, user_id: user.uid });
    setNewItem('');
    setShowValidationPopup(false);
    setItemToAddAfterValidation(null);
    setIsGeminiProcessing(false);
    if (validationPromiseCallbacks) {
      validationPromiseCallbacks.resolve(true);
      setValidationPromiseCallbacks(null);
    }
  };

  const handleUpdateItemNameConfirmed = async () => {
    if (!user || !itemToUpdateAfterValidation) return;

    const { item, newName } = itemToUpdateAfterValidation;
    if (!item.id) return;

    const itemRef = doc(db, 'cartItems', item.id);
    await updateDoc(itemRef, { item_name: newName });
    window.gtag('event', 'item_updated', { item_id: item.id, item_name: newName, user_id: user.uid, updated_fields: ['item_name'] });

    setShowValidationPopup(false);
    setItemToUpdateAfterValidation(null);
    setIsGeminiProcessing(false);
    if (validationPromiseCallbacks) {
      validationPromiseCallbacks.resolve(true);
      setValidationPromiseCallbacks(null);
    }
  };

  async function callGenerativeAi(prompt: string, apiKey: string) {
    const MAX_RETRIES = 5;
    let retries = 0;
    let delay = 10000; // 10 seconds
    let modelName = 'gemini-3.1-flash-lite';

    while (retries < MAX_RETRIES) {
      try {
        const response = await fetch(`https://generativelanguage.googleapis.com/v1beta/models/${modelName}:generateContent?key=${apiKey}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            "contents": [
              {
                "parts": [
                  { "text": prompt }
                ]
              }
            ]
          })
        });

        const data = await response.json();

        if (response.status === 429 || response.status === 503 || (data.error && data.error.status === 'RESOURCE_EXHAUSTED')) {
          console.warn(`Gemini API quota exceeded or rate limited. Retrying in ${delay / 1000} seconds...`);

          // If we are currently using the standard flash model, try switching to lite
          if (modelName === 'gemini-2.5-flash-lite') {
            console.log("Switching to gemini-2.5-flash-lite model for fallback.");
            modelName = 'gemini-2.5-flash-lite';
          }

          retries++;
          // eslint-disable-next-line no-loop-func
          await new Promise(resolve => setTimeout(resolve, delay));
          delay *= 2; // Exponential backoff
          continue;
        }

        if (data.candidates && data.candidates.length > 0 && data.candidates[0].content && data.candidates[0].content.parts && data.candidates[0].content.parts.length > 0) {
          return data.candidates[0].content.parts[0].text;
        } else {
          console.error("Gemini API response did not contain expected candidates or content:", data);
          if (data.error && data.error.message) {
            throw new Error(`Gemini API Error: ${data.error.message}`);
          } else {
            throw new Error("Gemini API response missing candidates or content.");
          }
        }
      } catch (error) {
        console.error("Error calling Gemini API:", error);
        throw error; // Re-throw to propagate the error
      }
    }
    throw new Error(`Failed to call Gemini API after ${MAX_RETRIES} retries due to quota issues or rate limiting.`);
  }

  const validateAndSelectWebsites = async (itemName: string, apiKey: string) => {
    // websites is string[]
    const all_available_names = websites.join(", ");

    const combinedPrompt = `You are an expert shopping assistant.
Your task is to:
1. Validate if the item is a specific physical product or purchasable service.
2. Select the most relevant websites from the provided list to find this item.

Item: "${itemName}"
Available Websites: ${all_available_names}

Respond with a JSON object with the following keys:
- "is_shopping_item" (boolean): true if the item is a physical product or purchasable service, false otherwise.
- "is_specific_enough" (boolean): true if the item includes key details (brand, model, etc.) for a specific search, false otherwise.
- "reason" (string): Concise explanation if specific/valid checks fail. If valid, leave empty.
- "websites" (array of strings): List of UP TO 5 website names from the "Available Websites" list that are best for finding this item. SELECT THESE EVEN IF THE ITEM IS NOT SPECIFIC ENOUGH (make a best guess based on the category of the item).

Example 1 (Valid):
Item: "iPhone 16 Pro 256GB"
{
  "is_shopping_item": true,
  "is_specific_enough": true,
  "reason": "",
  "websites": ["Amazon", "Best Buy", "Target"]
}

Example 2 (Vague):
Item: "laptop"
{
  "is_shopping_item": true,
  "is_specific_enough": false,
  "reason": "Too generic. Please specify brand/model.",
  "websites": ["Amazon", "Best Buy", "Newegg", "Walmart"]
}

Example 3 (Invalid):
Item: "happiness"
{
  "is_shopping_item": false,
  "is_specific_enough": false,
  "reason": "Abstract concept.",
  "websites": []
}
`;

    try {
      const geminiResponse = await callGenerativeAi(combinedPrompt, apiKey);
      const cleanedResponse = geminiResponse.replace(/```json\n|```/g, '').trim();
      const data = JSON.parse(cleanedResponse);

      // Ensure websites is always an array
      if (!data.websites || !Array.isArray(data.websites)) {
        data.websites = ["Amazon", "Walmart", "Target", "Costco"];
      }

      return data;
    } catch (error) {
      console.error("Error validating/selecting websites with Gemini: ", error);
      let errorMessage = "An unknown error occurred.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      // Fallback: Return failure but with default websites just in case
      const defaultSites = ["Amazon", "Walmart", "Target", "Costco"].filter(name => websites.includes(name));
      return {
        is_shopping_item: false,
        is_specific_enough: false,
        reason: `Validation failed: ${errorMessage}`,
        websites: defaultSites
      };
    }
  };


  const handleAddItem = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newItem.trim() === '' || !user) return;

    // Check if geminiApiKey is present
    if (!geminiApiKey) {
      setShowGlobalSettingsPopup(true);
      return;
    }

    setIsGeminiProcessing(true);
    let useAi = true;
    const currentSmartCartItemsCount = shoppingItems.filter(item => item.use_ai_to_find_product === true || item.use_ai_to_find_product === undefined).length;

    if (currentSmartCartItemsCount >= maxItemsInSmartCart) {
      useAi = false;
      setItemToDisableAI({ id: 'new-item-dummy', item_name: newItem, userId: user.uid, use_ai_to_find_product: false } as Item);
      setShowQuotaReachedPopup(true);
    }

    let is_shopping_item = true; // Default to true if skipping AI
    let is_specific_enough = true;
    let failure_reason = "";
    let selected_websites: string[] = ["Amazon", "Walmart", "Target", "Costco"];

    if (useAi) {
      const result: any = await validateAndSelectWebsites(newItem, geminiApiKey);
      console.log("Gemini Result:", result);

      is_shopping_item = result.is_shopping_item;
      is_specific_enough = result.is_specific_enough;
      failure_reason = result.reason;

      if (result.websites && result.websites.length > 0) {
        selected_websites = result.websites;
      }
    }

    setIsGeminiProcessing(false);

    if (useAi && (!is_shopping_item || !is_specific_enough)) {
      console.log("Validation failed:", failure_reason);
      setValidationMessage(failure_reason);
      // Pass the selected websites to the validation popup so it can be used if user overrides
      setItemToAddAfterValidation({
        item_name: newItem,
        userId: user.uid,
        use_ai_to_find_product: useAi,
        selected_websites: selected_websites,
        total_sites: selected_websites.length
      } as Item);
      setShowValidationPopup(true);
      return;
    }

    const newItemData = {
      item_name: newItem,
      userId: user.uid,
      last_updated: new Date(0).toISOString(),
      is_tracked_periodically: false,
      use_ai_to_find_product: useAi,
      selected_websites: selected_websites, // Save selected websites
      total_sites: selected_websites.length // Pre-calculate total sites
    };

    const docRef = await addDoc(collection(db, 'cartItems'), newItemData);
    window.gtag('event', 'item_added', { item_id: docRef.id, item_name: newItemData.item_name, user_id: user.uid });
    setNewItem('');
  };

  const handleUpdateItemName = async (itemToUpdate: Item, newName: string): Promise<boolean> => {
    if (!user || !itemToUpdate.id) return false;

    // Check if geminiApiKey is present
    if (!geminiApiKey) {
      setShowGlobalSettingsPopup(true);
      return false;
    }

    setIsGeminiProcessing(true);
    // Use the new combined function, but only care about validation results
    const validationResult: any = await validateAndSelectWebsites(newName, geminiApiKey);
    setIsGeminiProcessing(false);
    console.log("Validation result for update:", validationResult);

    if (!validationResult.is_shopping_item || !validationResult.is_specific_enough) {
      console.log("Validation failed for update:", validationResult);
      setValidationMessage(validationResult.reason);
      setItemToUpdateAfterValidation({ item: itemToUpdate, newName: newName });
      setShowValidationPopup(true);

      return new Promise<boolean>((resolve, reject) => {
        setValidationPromiseCallbacks({ resolve, reject });
      });
    }

    const itemRef = doc(db, 'cartItems', itemToUpdate.id);
    await updateDoc(itemRef, { item_name: newName });
    return true;
  };

  const handleDeleteItem = async (itemToRemove: Item) => {
    if (!user || !itemToRemove.id) return;
    const itemRef = doc(db, 'cartItems', itemToRemove.id);
    await deleteDoc(itemRef);
    window.gtag('event', 'item_deleted', { item_id: itemToRemove.id, item_name: itemToRemove.item_name, user_id: user.uid });
  };

  const handleRefreshItem = async (itemToRefresh: Item) => {
    if (!user || !itemToRefresh.id) return;
    const itemRef = doc(db, 'cartItems', itemToRefresh.id);
    await updateDoc(itemRef, {
      last_updated: new Date(0).toISOString(),
      in_processing: false,
      price: deleteField(),
      link: deleteField(),
      reason: deleteField(),
      error: deleteField(),
      website_results: deleteField(),
    });
    window.gtag('event', 'item_refreshed', { item_id: itemToRefresh.id, item_name: itemToRefresh.item_name, user_id: user.uid });
  };

  const handleSaveSettings = async (itemToUpdate: Item, settings: { is_tracked_periodically: boolean; selected_websites: string[]; use_ai_to_find_product: boolean }) => {
    if (!user) return;

    // Check quota if user is trying to enable AI
    if (settings.use_ai_to_find_product && (itemToUpdate.use_ai_to_find_product === false || itemToUpdate.use_ai_to_find_product === undefined)) {
      const currentSmartCartItemsCount = shoppingItems.filter(item => item.use_ai_to_find_product === true || item.use_ai_to_find_product === undefined).length;
      if (currentSmartCartItemsCount >= maxItemsInSmartCart) {
        setItemToDisableAI(itemToUpdate); // Store item to disable AI later
        setShowQuotaReachedPopup(true);
        return; // Prevent saving changes
      }
    }

    if (!itemToUpdate.id) return;

    const itemRef = doc(db, 'cartItems', itemToUpdate.id);
    const updatedFields: { [key: string]: any } = {};

    // Check for changes in is_tracked_periodically
    if (itemToUpdate.is_tracked_periodically !== settings.is_tracked_periodically) {
      updatedFields.is_tracked_periodically = settings.is_tracked_periodically;
    }

    // Check for changes in use_ai_to_find_product
    if (itemToUpdate.use_ai_to_find_product !== settings.use_ai_to_find_product) {
      updatedFields.use_ai_to_find_product = settings.use_ai_to_find_product;
    }

    // Check for changes in selected_websites
    const currentSelectedWebsites = itemToUpdate.selected_websites || [];
    const newSelectedWebsites = settings.selected_websites || [];

    if (JSON.stringify(currentSelectedWebsites.sort()) !== JSON.stringify(newSelectedWebsites.sort())) {
      if (newSelectedWebsites.length === 0) {
        updatedFields.selected_websites = deleteField();
      } else {
        updatedFields.selected_websites = newSelectedWebsites;
      }
    }

    if (Object.keys(updatedFields).length > 0) {
      await updateDoc(itemRef, updatedFields);
      window.gtag('event', 'item_updated', { item_id: itemToUpdate.id, item_name: itemToUpdate.item_name, user_id: user.uid, updated_fields: Object.keys(updatedFields) });
    }
  };

  const handleQuotaConfirm = async () => {
    if (!user || !itemToDisableAI) return;

    // If it's a dummy item for a new entry, we don't need to update Firestore
    if (itemToDisableAI.id === 'new-item-dummy') {
      setItemToDisableAI(null); // Clear the stored item
      return;
    }

    if (!itemToDisableAI.id) return;
    const itemRef = doc(db, 'cartItems', itemToDisableAI.id);
    await updateDoc(itemRef, {
      use_ai_to_find_product: false,
    });
    setItemToDisableAI(null); // Clear the stored item
  };

  const handleGlobalSettingsSave = async (settings: { tabAutoCloseTimeoutMinutes: number; maxParallelTabs: number; defaultTopProducts: number; geminiApiKey: string | null }) => {
    if (!user) return;
    const userSettingsRef = doc(db, 'userSettings', user.uid);
    try {
      await setDoc(userSettingsRef, {
        tabAutoCloseTimeoutMinutes: settings.tabAutoCloseTimeoutMinutes,
        maxParallelTabs: settings.maxParallelTabs,
        defaultTopProducts: settings.defaultTopProducts,
        geminiApiKey: settings.geminiApiKey,
      }, { merge: true });
      setGeminiApiKey(settings.geminiApiKey);
      setShowGlobalSettingsPopup(false);
    } catch (error) {
      console.error("Error saving global settings: ", error);
    }
  };

  const unprocessedItems = shoppingItems.filter(item =>
    !item.website_results &&
    !item.error &&
    (item.use_ai_to_find_product === true || item.use_ai_to_find_product === undefined)
  );
  const itemsProcessingFine = shoppingItems.filter(item => (item.website_results || item.error) && (item.use_ai_to_find_product === true || item.use_ai_to_find_product === undefined));

  // Items where AI is not used, including non-shopping items not using AI
  const itemsNotUsingAI = shoppingItems.filter(item => item.use_ai_to_find_product === false);

  const renderLoggedOutView = () => (
    <div className="logged-out-view">
      <div className="text-center mb-5">
        <img src="/logo.png" alt="AI Shopping Cart Logo" className="app-logo" />
        <h1>AI Shopping Cart</h1>
        <p className="lead">
          Your intelligent shopping assistant. Find the best prices and options automatically across your favorite stores.
        </p>
        <div className="d-grid gap-3 d-sm-flex justify-content-sm-center">
          <button className="btn btn-primary btn-lg px-5 shadow-sm" onClick={handleGoogleLogin}>
            <i className="bi bi-google me-2"></i> Continue with Google
          </button>
        </div>
      </div>
      <div className="row g-4 mt-5 justify-content-center w-100" style={{ maxWidth: '800px' }}>
        <div className="col-md-4 text-center">
          <div className="p-3">
            <i className="bi bi-magic fs-1 text-primary mb-3 d-block"></i>
            <h3 className="h5">AI Powered</h3>
            <p className="small text-muted">Smart algorithms find the exact products you need.</p>
          </div>
        </div>
        <div className="col-md-4 text-center">
          <div className="p-3">
            <i className="bi bi-search fs-1 text-primary mb-3 d-block"></i>
            <h3 className="h5">Auto Research</h3>
            <p className="small text-muted">Saves you hours of manual price comparisons.</p>
          </div>
        </div>
        <div className="col-md-4 text-center">
          <div className="p-3">
            <i className="bi bi-lightning fs-1 text-primary mb-3 d-block"></i>
            <h3 className="h5">Fast & Efficient</h3>
            <p className="small text-muted">Get results in seconds across multiple retailers.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderLoggedInView = () => (
    <div className="container py-5">
      <div className="mb-5 text-center text-md-start">
        <h1 className="h2 mb-1 fw-bold">My Shopping List</h1>
        <p className="text-muted mb-0">Manage your items and let AI find the best deals.</p>
      </div>

      <div className="card border-0 shadow-sm mb-5 overflow-visible">
        <div className="card-body p-4">
          <form onSubmit={handleAddItem}>
            <div className="row g-3">
              <div className="col-12 col-md">
                <div className="position-relative">
                  <i className="bi bi-plus-circle position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                  <input
                    type="text"
                    className="form-control form-control-lg ps-5 border-2"
                    placeholder="What are you looking for?"
                    value={newItem}
                    onChange={(e) => setNewItem(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-12 col-md-auto">
                <button
                  className="btn btn-primary btn-lg px-4 d-flex align-items-center justify-content-center w-100"
                  type="submit"
                  disabled={isGeminiProcessing}
                >
                  {isGeminiProcessing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-lg me-2"></i>
                      Add Item
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <div className="d-grid gap-4">
        <div className="card border-0 shadow-sm mb-0">
          <div className="card-header bg-white py-3 border-bottom-0">
            <div className="d-flex align-items-center">
              <div className="bg-primary bg-opacity-10 p-2 rounded-3 me-3">
                <i className="bi bi-stars text-primary"></i>
              </div>
              <h2 className="h5 mb-0 fw-bold">Smart Cart</h2>
              <i
                className="bi bi-info-circle ms-2 text-muted"
                onMouseEnter={() => setShowSmartCartInfo(true)}
                onMouseLeave={() => setShowSmartCartInfo(false)}
                style={{ cursor: 'pointer', fontSize: '0.875rem' }}
              ></i>
            </div>
            {showSmartCartInfo && (
              <div className="alert alert-primary py-2 px-3 mt-3 mb-0 small border-0 bg-opacity-10 text-primary">
                <i className="bi bi-lightbulb me-2"></i>
                We use AI to automatically find and track the best product matches for these items.
              </div>
            )}
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <CartTable
                title=""
                items={[...unprocessedItems, ...itemsProcessingFine]}
                onUpdate={handleUpdateItemName}
                onDelete={handleDeleteItem}
                onRefresh={handleRefreshItem}
                onSaveSettings={handleSaveSettings}
                columns={['Item Name', 'Last Updated', 'Price/Link', 'Actions']}
                isGeminiProcessing={isGeminiProcessing}
                userId={user?.uid || null}
              />
            </div>
          </div>
        </div>

        {itemsNotUsingAI.length > 0 && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white py-3 border-bottom-0">
              <div className="d-flex align-items-center">
                <div className="bg-secondary bg-opacity-10 p-2 rounded-3 me-3">
                  <i className="bi bi-cart text-secondary"></i>
                </div>
                <h2 className="h5 mb-0 fw-bold">Regular List</h2>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <CartTable
                  title=""
                  items={itemsNotUsingAI}
                  onUpdate={handleUpdateItemName}
                  onDelete={handleDeleteItem}
                  onSaveSettings={handleSaveSettings}
                  columns={['Item Name', 'Last Updated', 'Actions']}
                  isGeminiProcessing={isGeminiProcessing}
                  userId={user?.uid || null}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="App">
      <Header 
        user={user} 
        onLogout={handleLogout} 
        onShowSettings={() => setShowGlobalSettingsPopup(true)} 
      />
      <main className="flex-grow-1">
        <Routes>
          <Route path="/" element={user ? renderLoggedInView() : renderLoggedOutView()} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        </Routes>
      </main>
      <Footer onShowAbout={() => setShowAboutPopup(true)} />
      {showAboutPopup && <AboutPopup onClose={() => setShowAboutPopup(false)} />}
      {showExtensionRequiredPopup && <ExtensionRequiredPopup onClose={() => setShowExtensionRequiredPopup(false)} />}
      {showQuotaReachedPopup && (
        <QuotaReachedPopup
          onClose={() => setShowQuotaReachedPopup(false)}
          onConfirm={handleQuotaConfirm}
        />
      )}
      {showGlobalSettingsPopup && (
        <GlobalSettingsPopup
          onClose={() => setShowGlobalSettingsPopup(false)}
          geminiApiKey={geminiApiKey}
          onSave={handleGlobalSettingsSave}
        />
      )}
      {showValidationPopup && (
        <ValidationPopup
          message={validationMessage}
          onClose={() => {
            setShowValidationPopup(false);
            setIsGeminiProcessing(false);
            if (validationPromiseCallbacks) {
              validationPromiseCallbacks.resolve(false);
              setValidationPromiseCallbacks(null);
            }
          }}
          onConfirm={itemToAddAfterValidation ? handleAddItemConfirmed : handleUpdateItemNameConfirmed}
          type={itemToAddAfterValidation ? 'add' : 'edit'}
        />
      )}
    </div>
  );
}

export default App;